// Coalesces client-name lookups requested during render bursts into bulk
// POST /users/bulk-details calls. A trailing timer (not reset per enqueue)
// bounds flush latency even under a continuous stream of renders, and one
// screenful of rooms produces exactly one request instead of N.

const FLUSH_DELAY_MS = 25;
const MAX_IDS_PER_CALL = 100; // backend 422s above this

/**
 * Creates a shared collector for lazily resolving client display names.
 *
 * `enqueue` is safe to call during render: it only mutates module-local Sets
 * and schedules a timer. Results flow back through `onNames`, which the owner
 * uses to merge into its cache and re-render subscribers — once per chunk.
 *
 * @param {Object} deps
 * @param {(ids: string[]) => Promise<{success: boolean, data?: object, message?: string}>} deps.fetchBulk -
 *   Bulk lookup, e.g. `getBulkUserDetails`. Receives ≤100 ids per call.
 * @param {(updates: Object<string, string>) => void} deps.onNames -
 *   Called with `{ [userId]: name }` for each resolved chunk.
 * @param {(id: string) => boolean} deps.hasName - True if the id is already cached.
 * @param {(firstName: string, lastName: string) => string|undefined} deps.buildName -
 *   Display-name builder; undefined means "no usable name".
 * @returns {{ enqueue: (clientId: string|number|null|undefined) => void }}
 */
export const createClientNameBatcher = ({ fetchBulk, onNames, hasName, buildName }) => {
  const pending = new Set(); // queued for the next flush
  const inFlight = new Set(); // part of an active request
  // Server-confirmed unknown ids (and users with no name set) — skipped for
  // the rest of this page load. Deliberately not persisted, so they retry on
  // the next load in case the profile has since been filled in.
  const missing = new Set();
  let timer = null;

  const requestChunk = async (chunk) => {
    try {
      const res = await fetchBulk(chunk);
      if (!res?.success) throw new Error(res?.message || "bulk user details failed");
      // The API envelope can carry success:false on a 200 — treat it as a
      // failure too, or the ids would silently re-enqueue forever unlogged.
      const envelope = res.data;
      if (envelope && envelope.success === false) {
        throw new Error(envelope.error || "bulk user details returned success: false");
      }
      const body = envelope?.data || {};
      const updates = {};
      (body.users || []).forEach((user) => {
        if (user?.user_id == null) return;
        const name = buildName(user.first_name, user.last_name);
        if (name) updates[String(user.user_id)] = name;
        else missing.add(String(user.user_id));
      });
      (body.missing_ids || []).forEach((id) => missing.add(String(id)));
      if (Object.keys(updates).length) onNames(updates);
    } catch (err) {
      // Failed ids stay out of `missing`, so a future render re-queues them —
      // retries are bounded by the flush window and by screens settling.
      console.error("useCommsIdentity: bulk client lookup failed", err);
    } finally {
      chunk.forEach((id) => inFlight.delete(id));
    }
  };

  const flush = () => {
    timer = null;
    // Recheck the cache at flush time: ids enqueued during a first render can
    // be satisfied by the localStorage hydration that runs in effects, before
    // this timer fires — a warm reload then makes zero network calls.
    const ids = [...pending].filter((id) => !hasName(id));
    pending.clear();
    if (!ids.length) return;
    ids.forEach((id) => inFlight.add(id));
    for (let i = 0; i < ids.length; i += MAX_IDS_PER_CALL) {
      requestChunk(ids.slice(i, i + MAX_IDS_PER_CALL));
    }
  };

  return {
    /** Queues a client id for bulk resolution. Safe to call during render. */
    enqueue(clientId) {
      if (clientId == null) return;
      const key = String(clientId).trim();
      if (!key) return;
      if (hasName(key) || pending.has(key) || inFlight.has(key) || missing.has(key)) return;
      pending.add(key);
      if (!timer) timer = setTimeout(flush, FLUSH_DELAY_MS);
    },
  };
};
